import { Controller, useFormContext } from "react-hook-form";
import { EditCreateFolderFormValues } from "../hooks/useEditCreateFolderZodSchema";
import { Box, Collapse, Stack, Typography, alpha } from "@mui/material";
import { useTranslation } from "react-i18next";
import { TransitionGroup } from "react-transition-group";
import TeamInFolder from "./TeamInFolder";
import MemberInFolder from "./MemberInFolder";
import BetterCheckbox from "../../../../../utils/components/BetterCheckbox";
import CertiblokIcon from "../../../../../utils/components/icons/CertiblokIcon";
import WhitePopperTooltip from "../../../../../utils/components/poppers/WhitePopperTooltip";

export const NewlyInvitedMembersList = () => {
	const { t } = useTranslation();

	const { watch, setValue } = useFormContext<EditCreateFolderFormValues>();
	const reservedToUsers = watch("reservedToUsers");
	const reservedToTeams = watch("reservedToTeams");

	const newlyInvitedUsers = reservedToUsers.filter((user) => user.isNew);
	const newlyInvitedTeams = reservedToTeams.filter((team) => team.isNew);

	const hasNewMembers = newlyInvitedUsers.length > 0 || newlyInvitedTeams.length > 0;

	return (
		<Collapse in={hasNewMembers}>
			<Box
				sx={{
					mt: 4,
					p: 3,
					borderRadius: 3,
					backgroundColor: (theme) => alpha(theme.palette.secondary.main, 0.04),
					border: "1px solid",
					borderColor: (theme) => alpha(theme.palette.secondary.main, 0.1),
				}}
			>
				<Stack direction="row" alignItems="center" spacing={1} mb={2}>
					<CertiblokIcon name="profile_outline_01" size={20} color="secondary" />
					<Typography
						variant="subtitle2"
						sx={{
							color: (theme) => theme.palette.text.primary,
							fontWeight: 600,
						}}
					>
						{t("folders.newlyInvitedMembers")}
					</Typography>
				</Stack>

				<Controller
					name="giveAccessToSubfolders"
					render={({ field }) => {
						return (
							<Stack
								direction="row"
								alignItems="center"
								spacing={1.5}
								sx={{
									p: 1.5,
									borderRadius: 2,
									backgroundColor: (theme) => alpha(theme.palette.common.white, 0.6),
									border: "1px solid",
									borderColor: (theme) => alpha(theme.palette.secondary.main, 0.08),
									mb: 2,
								}}
							>
								<BetterCheckbox color="secondary" checked={field.value} onClick={() => field.onChange(!field.value)} />
								<Stack spacing={2} direction="row" alignItems="center">
									<Typography
										variant="body2"
										sx={{
											color: (theme) => theme.palette.text.primary,
											fontWeight: 500,
										}}
									>
										{t("folders.wouldYouLikeToGiveAccessToSubfolders")}
									</Typography>
									<WhitePopperTooltip enabled title={t("folders.accessToSubfoldersExplanation")}>
										<CertiblokIcon size={16} color="#0000008A" name={"comunication_outline_02"} />
									</WhitePopperTooltip>
								</Stack>
							</Stack>
						);
					}}
				/>

				<TransitionGroup>
					{newlyInvitedTeams.map((team, index) => (
						<Collapse key={team?.id} enter={index > 0}>
							<TeamInFolder
								team={team}
								endIcon="delete"
								onEndIconClick={() => {
									setValue(
										"reservedToTeams",
										reservedToTeams.filter((selectedGroup) => selectedGroup.id !== team.id)
									);
								}}
								sx={{
									marginTop: 1,
								}}
							/>
						</Collapse>
					))}
					{newlyInvitedUsers.map((user, index) => (
						<Collapse key={user?.id} enter={index > 0 || newlyInvitedTeams.length > 0}>
							<MemberInFolder
								user={user}
								endIcon="delete"
								onEndIconClick={() => {
									setValue(
										"reservedToUsers",
										reservedToUsers.filter((selectedUsers) => selectedUsers.id !== user.id)
									);
								}}
								sx={{
									marginTop: 1,
								}}
							/>
						</Collapse>
					))}
				</TransitionGroup>
			</Box>
		</Collapse>
	);
};
